import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, X } from "lucide-react";
import { useCan } from "@/auth/permissions";
import { FixEnglishLink, isMissingEnglish } from "@/components/MissingEnglish";
import { marketingApi, MKT_QK, type SocialProduct } from "./api";

/**
 * MKT-1 · 产品开放清单（社媒开关）——内容营销「产品开放」页签 + 上架中心「社媒（S4）」页签共用这一个组件（契约 V1.1 §B）。
 * 列：产品 / 社媒开放（开关）/ 被几条帖子挂着（S4 /posts，拿不到 =「未知」）/ 可用图片（已授权 N 张 · 未声明 N 张）/ 去图片库标授权。
 * 开→关先确认（关掉 = 对 S4 等于下架，没发出去的帖子会被拦住）；改开关要 listing.mappings.manage，没有就只读。
 * 接口：GET /api/v1/marketing/social-products · PUT /api/v1/marketing/social-products/{product_id}
 */
export function SocialProductsTable() {
  const qc = useQueryClient();
  const canManage = useCan("listing.mappings.manage");
  const q = useQuery({ queryKey: MKT_QK.products, queryFn: marketingApi.socialProducts });
  const [confirmOff, setConfirmOff] = useState<SocialProduct | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // ENNAME-3 · 打开时缺英文名（409）→ 这一行开关旁给「去补」；打开成功但行程里景点 / 票种缺英文名 → 黄色提示，不拦
  const [missingFor, setMissingFor] = useState<string | null>(null);
  const [warns, setWarns] = useState<{ code: string; items: string[] } | null>(null);
  const mut = useMutation({
    mutationFn: ({ id, on }: { id: string; on: boolean; code: string }) => marketingApi.setSocial(id, on),
    onSuccess: (res, v) => {
      setConfirmOff(null); setErr(null); setMissingFor(null);
      setWarns(res.warnings?.length ? { code: v.code, items: res.warnings } : null);
      qc.invalidateQueries({ queryKey: MKT_QK.all });
    },
    onError: (e, v) => {
      setConfirmOff(null); setWarns(null);
      setMissingFor(isMissingEnglish(e) ? v.id : null);
      setErr(e instanceof Error ? e.message : "保存失败，请重试");
    },
  });
  const toggle = (p: SocialProduct) => {
    if (p.social_enabled) setConfirmOff(p);
    else mut.mutate({ id: p.product_id, on: true, code: p.code });
  };
  const items = q.data?.items ?? [];

  return (
    <>
      {err && <div className="mx-3 mt-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[12.5px] text-rose-700" role="alert">{err}</div>}
      {warns && (
        <div className="mx-3 mt-2 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12.5px] text-amber-700" role="status" data-testid="social-name-warning">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" /><span>{warns.code}：{warns.items.join("；")}</span>
        </div>
      )}
      <div className="overflow-x-auto" data-testid="social-products">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="bg-slate-50 text-left text-slate-400">
              <th className="h-8 px-2.5 font-medium">产品</th><th className="px-2.5 font-medium">社媒开放</th>
              <th className="px-2.5 font-medium">被几条帖子挂着</th><th className="px-2.5 font-medium">可用图片</th><th className="px-2.5" />
            </tr>
          </thead>
          <tbody>
            {q.isLoading && <tr><td colSpan={5} className="py-6 text-center text-slate-400">加载中…</td></tr>}
            {q.isError && <tr><td colSpan={5} className="py-6 text-center text-rose-600">加载失败</td></tr>}
            {q.data && items.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-slate-400">没有已上架的产品</td></tr>}
            {items.map((p) => (
              <tr key={p.product_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid="social-row">
                <td className="h-[34px] whitespace-nowrap px-2.5"><b>{p.code}</b> {p.name_cn}</td>
                <td className="px-2.5">
                  <button type="button" role="switch" aria-checked={p.social_enabled} aria-label={`${p.code} 社媒开放`} data-testid="social-toggle"
                    disabled={!canManage || mut.isPending} onClick={() => toggle(p)}
                    className={`relative inline-flex h-[18px] w-8 shrink-0 rounded-full transition disabled:cursor-not-allowed disabled:opacity-60 ${p.social_enabled ? "bg-brand" : "bg-slate-300"}`}>
                    <span className={`absolute top-0.5 h-3.5 w-3.5 rounded-full bg-white transition-all ${p.social_enabled ? "left-4" : "left-0.5"}`} />
                  </button>
                  {missingFor === p.product_id && <FixEnglishLink productId={p.product_id} />}
                </td>
                <td className="whitespace-nowrap px-2.5" data-testid="social-mounted">
                  {p.mounted_posts === null ? <span className="text-slate-400" title={q.data?.s4.label ?? undefined}>未知</span> : p.mounted_posts === 0 && !p.social_enabled ? "—" : p.mounted_posts}
                </td>
                <td className="whitespace-nowrap px-2.5" data-testid="social-images">
                  {p.images_undeclared > 0 ? (
                    <span className="rounded bg-amber-50 px-2 py-0.5 text-amber-800">{p.images_licensed} 张已授权 · {p.images_undeclared} 张未声明授权</span>
                  ) : `${p.images_licensed} 张已授权`}
                </td>
                <td className="whitespace-nowrap px-2.5 text-right">
                  {p.images_undeclared > 0 && (
                    <Link to="/media-library?licensed=undeclared" className="rounded-md border border-slate-200 px-2 py-0.5 text-[12px] text-slate-700 hover:border-brand hover:text-brand">去图片库标授权</Link>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {confirmOff && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm animate-in fade-in" role="dialog" aria-label="关闭社媒开放"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setConfirmOff(null); }}>
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl animate-in zoom-in-95 slide-in-from-bottom-4" data-testid="social-off-confirm">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-base font-semibold text-slate-900">关掉 {confirmOff.code} 的社媒开放？</h3>
              <button type="button" aria-label="关闭" onClick={() => setConfirmOff(null)} className="rounded p-1 text-slate-400 hover:bg-slate-100"><X size={16} /></button>
            </div>
            <p className="text-[13px] leading-relaxed text-slate-600">关掉 = 对 S4 等于下架：没发出去的帖子会被拦住；已经发出去的帖子不会被撤回。以后再打开，产品编号不变。</p>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setConfirmOff(null)} className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-600">取消</button>
              <button type="button" data-testid="social-off-ok" disabled={mut.isPending} onClick={() => mut.mutate({ id: confirmOff.product_id, on: false, code: confirmOff.code })}
                className="rounded-md bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-50">确认关掉</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
